
import { ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { Project } from '../../../core/models/project/Project.model';
import { ApplicationService } from '../../../core/services/application.service';
import { ProjectService } from '../../../core/services/project.service';
import { EnmApplicationAvailabilityStatus } from '../../../core/models/enums/EnmApplicationAvailabilityStatus.enum';

type ProjectViewMode = 'card' | 'application';
type ProjectStatusType = 'green' | 'blue' | 'orange' | 'gray';

interface ApplicationGroup {
    name: string;
    icon: string;
    iconColor: string;
    iconBackground: string;
    projects: Project[];
}

@Component({
    selector: 'app-projects',
    standalone: false,
    templateUrl: './projectlist.component.html',
    styleUrl: './projectlist.component.scss'
})
export class ProjectListComponent {

    readonly AvailabilityStatus = EnmApplicationAvailabilityStatus;

    viewMode: ProjectViewMode = 'card';
    searchText = '';
    sortType = 'lastOpened';
    projects: Project[] = [];
    EnmApplicationAvailabilityStatus: any;

    constructor(
        private readonly router: Router,
        projectSvc: ProjectService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        appSvc: ApplicationService
    ) {

        projectSvc.GetAllProjects().subscribe((data) => {

            this.projects = data.data ?? [];

            this.changeDetectorRef.detectChanges();

        });

    }

    // Date, ISO string, timestamp ve boş değerleri güvenle işler.
    private getDateTimestamp(value: unknown): number {

        if (value === null || value === undefined || value === '') return 0;

        let date: Date;

        if (value instanceof Date) {
            date = value;
        }
        else if (typeof value === 'string') {
            date = new Date(value);
        }
        else if (typeof value === 'number') {
            date = new Date(value);
        }
        else {
            return 0;
        }

        const timestamp = date.getTime();

        return Number.isFinite(timestamp) ? timestamp : 0;
    }

    get filteredProjects(): Project[] {

        let result = [...this.projects];

        const search = this.searchText.trim().toLocaleLowerCase('tr-TR');

        if (search.length > 0) {

            result = result.filter(x =>
                x.name.toLocaleLowerCase('tr-TR').includes(search) ||
                x.description.toLocaleLowerCase('tr-TR').includes(search) ||
                x.applicationInfo?.applicationTypeText?.toLocaleLowerCase('tr-TR').includes(search) ||
                x.applicationInfo?.name?.toLocaleLowerCase('tr-TR').includes(search)
            );

        }

        if (this.sortType === 'name') {
            result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        }

        if (this.sortType === 'type') {
            result.sort((a, b) => a.projectType.toString().localeCompare(b.projectType.toString(), 'tr'));
        }

        if (this.sortType === 'lastOpened') {

            result.sort((a, b) =>
                this.getDateTimestamp(b.lastIncommingDataDate) -
                this.getDateTimestamp(a.lastIncommingDataDate)
            );

        }

        return result;
    }

    get applicationGroups(): ApplicationGroup[] {

        const groupDefinitions = [
            {
                name: 'CRM',
                icon: 'bi bi-window-stack',
                iconColor: '#7657f5',
                iconBackground: '#f0edff'
            },
            {
                name: 'API',
                icon: 'bi bi-code-slash',
                iconColor: '#18a87d',
                iconBackground: '#e9f9f3'
            },
            {
                name: 'Oyun',
                icon: 'bi bi-controller',
                iconColor: '#6655ee',
                iconBackground: '#eeeeff'
            },
            {
                name: 'AI',
                icon: 'bi bi-stars',
                iconColor: '#7658ef',
                iconBackground: '#f1edff'
            },
            {
                name: 'Web App',
                icon: 'bi bi-globe2',
                iconColor: '#315eea',
                iconBackground: '#edf2ff'
            },
            {
                name: 'Mobile App',
                icon: 'bi bi-phone',
                iconColor: '#24ab7c',
                iconBackground: '#eaf9f3'
            },
            {
                name: 'Masaüstü',
                icon: 'bi bi-display',
                iconColor: '#604bea',
                iconBackground: '#efedff'
            },
            {
                name: 'Mikroservis',
                icon: 'bi bi-box',
                iconColor: '#5749ed',
                iconBackground: '#efedff'
            }
        ];

        return groupDefinitions
            .map(definition => ({
                ...definition,
                projects: this.filteredProjects.filter(x => x.applicationInfo?.name === definition.name)
            }))
            .filter(x => x.projects.length > 0);
    }

    setViewMode(mode: ProjectViewMode): void {
        this.viewMode = mode;
    }

    onSearch(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.searchText = input.value;
    }

    onSortChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        this.sortType = select.value;
    }

    createProject(): void {
        this.router.navigate(['createproject']);
    }

    createBlankProject(): void {
        this.router.navigate(['createproject']);
    }

    createFromTemplate(): void {
        this.router.navigate(['createproject']);
        return;
    }

    importProject(): void {
        this.router.navigate(['createproject']);
        return;
    }

    openProject(project: Project): void {
        this.router.navigate(['/run', project.id]);
    }

    openProjectInfo(project: Project): void {
        this.router.navigate(['/project/' + project.id]);
    }

    openProjectMenu(project: Project): void {
        console.log('Proje menüsü:', project);
    }

    openApplicationGroup(group: ApplicationGroup): void {
        this.searchText = '';
        console.log('Uygulama grubu:', group.name);
    }

    onImageError(event: Event): void {

        const image = event.target as HTMLImageElement;

        if (image.dataset['fallbackApplied']) return;

        image.dataset['fallbackApplied'] = 'true';

        image.src = 'assets/images/projects/project-default.jpg';
    }
}
